// /src/GuitarSimulator/guitarUtils.js

// 기타 설정
export const stringNotes = ['E2', 'A2', 'D3', 'G3', 'B3', 'E4'];
export const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

// 한국어 음표명
export const koreanNotes = {
  'C': '도', 'C#': '도#', 'D': '레', 'D#': '레#', 'E': '미',
  'F': '파', 'F#': '파#', 'G': '솔', 'G#': '솔#', 'A': '라',
  'A#': '라#', 'B': '시'
};

// 음표 주파수 (Hz)
export const frequencies = {
  'E2': 82.41, 'F2': 87.31, 'F#2': 92.50, 'G2': 98.00, 'G#2': 103.83, 'A2': 110.00, 'A#2': 116.54, 'B2': 123.47,
  'C3': 130.81, 'C#3': 138.59, 'D3': 146.83, 'D#3': 155.56, 'E3': 164.81, 'F3': 174.61, 'F#3': 185.00, 'G3': 196.00, 'G#3': 207.65, 'A3': 220.00, 'A#3': 233.08, 'B3': 246.94,
  'C4': 261.63, 'C#4': 277.18, 'D4': 293.66, 'D#4': 311.13, 'E4': 329.63, 'F4': 349.23, 'F#4': 369.99, 'G4': 392.00, 'G#4': 415.30, 'A4': 440.00, 'A#4': 466.16, 'B4': 493.88,
  'C5': 523.25, 'C#5': 554.37, 'D5': 587.33, 'D#5': 622.25, 'E5': 659.25, 'F5': 698.46, 'F#5': 739.99, 'G5': 783.99, 'G#5': 830.61, 'A5': 880.00, 'A#5': 932.33, 'B5': 987.77,
  'C6': 1046.50, 'C#6': 1108.73, 'D6': 1174.66
};

// 유틸리티 함수들
export const GuitarUtils = {
  // 디바이스 타입 확인
  isMobile: () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),

  // 프렛 개수 결정
  getMaxFret: (isMobile) => isMobile ? 6 : 21,

  // 음표 계산
  calculateNote: (string, fret) => {
    const baseNote = stringNotes[string];
    const noteName = baseNote.slice(0, -1);
    let octave = parseInt(baseNote.slice(-1));

    const noteIndex = notes.indexOf(noteName);
    const newNoteIndex = (noteIndex + fret) % 12;
    const newOctave = octave + Math.floor((noteIndex + fret) / 12);

    return notes[newNoteIndex] + newOctave;
  },

  // 음표를 영어 알파벳으로 변환
  getNoteAlphabet: (note) => {
    const noteName = note.slice(0, -1);
    const alphabetMap = {
      'C': 'C', 'C#': 'C#', 'D': 'D', 'D#': 'D#', 'E': 'E',
      'F': 'F', 'F#': 'F#', 'G': 'G', 'G#': 'G#', 'A': 'A',
      'A#': 'A#', 'B': 'B'
    };
    return alphabetMap[noteName] || noteName;
  },

  // 음표를 한글로 변환 (샤프/플랫 제외)
  getKoreanNoteSimple: (note) => {
    const noteName = note.slice(0, -1);
    const koreanMap = {
      'C': '도', 'C#': '도', 'D': '레', 'D#': '레', 'E': '미',
      'F': '파', 'F#': '파', 'G': '솔', 'G#': '솔', 'A': '라',
      'A#': '라', 'B': '시'
    };
    return koreanMap[noteName] || noteName;
  },

  // 샤프/플랫이 아닌 자연음인지 확인
  isNaturalNote: (note) => {
    const noteName = note.slice(0, -1);
    return !noteName.includes('#') && !noteName.includes('b');
  },

  // 음표 표시 (한글/영문 선택, 자연음만)
  getDisplayNote: (note, useKorean = false) => {
    const noteName = note.slice(0, -1);
    if (useKorean) {
      const koreanMap = {
        'C': '도', 'C#': '도#', 'D': '레', 'D#': '레#', 'E': '미',
        'F': '파', 'F#': '파#', 'G': '솔', 'G#': '솔#', 'A': '라',
        'A#': '라#', 'B': '시'
      };
      return koreanMap[noteName] || '';
    } else {
      const englishMap = {
        'C': 'C', 'C#': 'C#', 'D': 'D', 'D#': 'D#', 'E': 'E',
        'F': 'F', 'F#': 'F#', 'G': 'G', 'G#': 'G#', 'A': 'A',
        'A#': 'A#', 'B': 'B'
      };
      return englishMap[noteName] || '';
    }
  },

  // 한국어 음표명 변환
  getKoreanNoteName: (note) => {
    const noteName = note.slice(0, -1);
    const octave = note.slice(-1);
    return `${koreanNotes[noteName] || noteName}${octave}`;
  },

  // 프렛 위치 계산
  getFretPosition: (fret, totalWidth, maxFret) => {
    if (fret === 0) return totalWidth;

    const ratio = Math.pow(2, 1/12);
    const scaleLength = totalWidth / (1 - Math.pow(ratio, -(maxFret + 2)));

    return totalWidth - scaleLength * (1 - Math.pow(ratio, -fret));
  },

  // 좌표 변환
  transformCoordinates: (x, y, canvas, canvasTransform) => {
    const rect = canvas.getBoundingClientRect();
    let canvasX = (x - rect.left) * (canvas.width / rect.width);
    let canvasY = (y - rect.top) * (canvas.height / rect.height);

    const w = canvas.offsetWidth * (canvas.width / rect.width);
    const h = canvas.offsetHeight * (canvas.height / rect.height);

    // 변환 역산
    if (canvasTransform.rotation === 180) {
      canvasX = w - canvasX;
      canvasY = h - canvasY;
    }

    if (canvasTransform.flipY) {
      canvasY = h - canvasY;
    }

    if (canvasTransform.flipX) {
      canvasX = w - canvasX;
    }

    return { x: canvasX, y: canvasY };
  },

  // 프렛과 현 계산
  getFretAndString: (canvasX, canvasY, canvas, maxFret) => {
    const stringHeight = canvas.height / 6;
    const string = Math.floor(canvasY / stringHeight);

    let fret = 0;

    if (canvasX > GuitarUtils.getFretPosition(1, canvas.width, maxFret)) {
      fret = 0;
    } else {
      for (let f = 1; f <= maxFret; f++) {
        const fretStart = GuitarUtils.getFretPosition(f, canvas.width, maxFret);
        const fretEnd = GuitarUtils.getFretPosition(f + 1, canvas.width, maxFret);
        if (canvasX <= fretStart && canvasX > fretEnd) {
          fret = f;
          break;
        }
      }
    }

    if (fret >= 0 && fret <= maxFret && string >= 0 && string < 6) {
      return { fret, string };
    }
    return null;
  },

  // 코드 구성음인지 확인 (줄 번호 체계 변환)
  isChordNote: (guitarUtilsStringIndex, fret, selectedChord, chordDefinitions) => {
    if (!selectedChord || !chordDefinitions[selectedChord]) return false;

    // guitarUtils 인덱스(0=6현)를 chordDefinitions 인덱스(1=1현)로 변환
    // guitarUtils: 0=6현, 1=5현, 2=4현, 3=3현, 4=2현, 5=1현
    // chordDefinitions: 1=1현, 2=2현, 3=3현, 4=4현, 5=5현, 6=6현
    const chordDefStringNumber = 6 - guitarUtilsStringIndex;
    
    return chordDefinitions[selectedChord].some(pos =>
      pos.string === chordDefStringNumber && pos.fret === fret
    );
  },

  // 음표 표시 업데이트
  updateNoteDisplay: (note, string, fret, selectedChord, getKoreanNoteName) => {
    const koreanNote = getKoreanNoteName(note);
    let displayText = `${koreanNote} (${note}) - ${string + 1}현 ${fret}프렛`;

    if (selectedChord) {
      displayText += ` | 선택된 코드: ${selectedChord}`;
    }

    return displayText;
  },

  // 줄 번호 변환 유틸리티 함수들
  // guitarUtils 배열 인덱스(0=6현)를 chordDefinitions 줄 번호(1=1현)로 변환
  convertToChordDefString: (guitarUtilsIndex) => {
    return 6 - guitarUtilsIndex;
  },

  // chordDefinitions 줄 번호(1=1현)를 guitarUtils 배열 인덱스(0=6현)로 변환  
  convertToGuitarUtilsIndex: (chordDefStringNumber) => {
    return 6 - chordDefStringNumber;
  },

  // 실제 기타 줄 번호(1=1현)를 표시용으로 변환
  getDisplayStringNumber: (guitarUtilsIndex) => {
    return 6 - guitarUtilsIndex;
  }
};